// useAutoScroll.js - Hook for auto-scrolling during drag operations
import { useEffect, useRef } from 'react';

/**
 * Hook to enable auto-scrolling when dragging near container edges
 * @param {boolean} isDragging - Whether a drag operation is in progress
 * @param {React.RefObject} containerRef - Reference to the scrollable container
 */
export const useAutoScroll = (isDragging, containerRef) => {
  const scrollIntervalRef = useRef(null);
  const lastMouseYRef = useRef(0);

  useEffect(() => {
    if (!isDragging || !containerRef?.current) {
      // Clear any existing scroll interval when not dragging
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      return;
    }

    const container = containerRef.current;
    const EDGE_SIZE = 150; // Distance from edge to trigger auto-scroll (pixels)
    const SCROLL_SPEED = 30; // Pixels to scroll per interval
    const SCROLL_INTERVAL = 6; // ~60fps

    const handleDragOver = (event) => {
      // Prevent default to allow drop
      event.preventDefault();

      // Store current mouse position
      lastMouseYRef.current = event.clientY;

      const rect = container.getBoundingClientRect();
      const mouseY = event.clientY - rect.top;
      const containerHeight = rect.height;

      // Check if near top edge
      const nearTop = mouseY < EDGE_SIZE;
      // Check if near bottom edge
      const nearBottom = mouseY > containerHeight - EDGE_SIZE;

      // Clear existing interval
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }

      // Start scrolling if near an edge
      if (nearTop || nearBottom) {
        scrollIntervalRef.current = setInterval(() => {
          if (nearTop && container.scrollTop > 0) {
            // Scroll up
            container.scrollTop -= SCROLL_SPEED;
          } else if (nearBottom && container.scrollTop < container.scrollHeight - container.clientHeight) {
            // Scroll down
            container.scrollTop += SCROLL_SPEED;
          }
        }, SCROLL_INTERVAL);
      }
    };

    // Add dragover listener to container
    container.addEventListener('dragover', handleDragOver);

    // Cleanup
    return () => {
      container.removeEventListener('dragover', handleDragOver);
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, [isDragging, containerRef]);
};
